import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Careers from "@/pages/Careers";

// A fresh page load always starts on the home page.
//
// On by default in development only. Turning it on in production would send
// anyone opening a shared /projects link straight to the homepage, and would
// show Googlebot the homepage at every URL in our sitemap — which would undo
// the per-page titles and canonicals. Set REACT_APP_ALWAYS_OPEN_HOME=true to
// force it everywhere.
const ALWAYS_OPEN_HOME =
  process.env.REACT_APP_ALWAYS_OPEN_HOME === "true" ||
  process.env.NODE_ENV === "development";

function OpenAtHome() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const handled = useRef(false);

  useEffect(() => {
    // Only the very first render: later in-app navigation must work normally.
    if (handled.current) return;
    handled.current = true;
    if (ALWAYS_OPEN_HOME && pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [pathname, navigate]);

  return null;
}

function App() {
  useEffect(() => {
    // Stop the browser jumping back to the previous scroll position on reload.
    // It restores before React mounts, so the page would open part-way down
    // instead of at the header.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);

    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <div className="noise-overlay" aria-hidden="true" />
      <BrowserRouter>
        <OpenAtHome />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:slug" element={<ProjectDetail />} />
          <Route path="/careers" element={<Careers />} />
          {/* Anything else (including the retired /admin) goes home. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
